import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatEUR } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, MetricCard, Section, EmptyState } from "@/components/ui-kit";
import { Toaster } from "sonner";
import CoachBrief from "@/components/CoachBrief";
import CoachLeaderboard from "@/components/CoachLeaderboard";
import { Users, Phone, Calendar, Flame, KeyRound, TrendingUp, FileSignature, Target, Award, Gauge, AlertTriangle, BellRing, PhoneCall, CalendarClock, ChevronRight } from "lucide-react";

export default function DashboardCommerce() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isVendeur = user?.role === "vendeur";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/dashboard/commerce").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-8 text-slate-500">Chargement…</div>;
  if (!data) return null;

  return (
    <div className="p-8" data-testid="dashboard-commerce">
      <Toaster position="top-right" richColors />
      <PageHeader title="Tableau de bord Commerce" subtitle="Espace Vendeurs" />

      <AlertesJour />

      {isVendeur && <CoachBrief />}

      {isVendeur && <CoachLeaderboard />}

      <ObjectifCard obj={data.objectif} />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <MetricCard label="Nouveaux prospects" value={data.nouveaux} icon={Users} tone="primary" testid="metric-nouveaux" to="/prospects?statut=nouveau" />
        <MetricCard label="À relancer" value={data.a_relancer} icon={Phone} tone="warning" testid="metric-a-relancer" to="/prospects?statut=a_relancer" />
        <MetricCard label="RDV à venir" value={data.rdv_a_venir} icon={Calendar} testid="metric-rdv" to="/rdv-planifies" />
        <MetricCard label="Prospects chauds" value={data.chauds} icon={Flame} tone="danger" testid="metric-chauds" to="/prospects?statut=chaud" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <MetricCard label="Options demandées" value={data.options_demandees} icon={KeyRound} testid="metric-options" to="/prospects?statut=option_demandee" />
        <MetricCard label="Ventes en cours" value={data.ventes_en_cours} icon={TrendingUp} tone="gold" testid="metric-ventes-cours" to="/prospects?statut=vente_en_cours" />
        <MetricCard label="Ventes signées" value={data.ventes_signees} icon={FileSignature} tone="success" testid="metric-signees" to="/prospects?statut=signe" />
        <MetricCard label="Total prospects" value={data.total_prospects} icon={Target} testid="metric-total" to="/prospects" />
      </div>

      <Section title="Pipeline par statut">
        <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-3">
          {data.by_status.length === 0 ? (
            <div className="col-span-full text-sm text-slate-500 text-center py-4">Aucun prospect enregistré.</div>
          ) : data.by_status.map((s) => (
            <Link key={s.statut} to={`/prospects?statut=${s.statut}`} className="border border-slate-200 p-3 rounded-sm bg-slate-50 hover:bg-white hover:border-[#B8873A] hover:shadow-sm transition-all duration-150 cursor-pointer group" data-testid={`status-${s.statut}`}>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold group-hover:text-[#B8873A]">{s.statut}</div>
              <div className="text-2xl font-bold text-[#111111]" style={{fontFamily:"Cabinet Grotesk"}}>{s.count}</div>
            </Link>
          ))}
        </div>
      </Section>

      <Section title="Performance par vendeur" actions={<Award className="w-4 h-4 text-[#B8873A]" />}>
        {(!data.performance_vendeurs || data.performance_vendeurs.length === 0) ? (
          <EmptyState icon={Award} title="Aucune donnée de performance" description="Créez des prospects et signez pour voir les stats." />
        ) : (
          <table className="w-full text-sm" data-testid="performance-table">
            <thead className="bg-slate-50 text-slate-700">
              <tr>
                <th className="text-left px-5 py-3 font-semibold uppercase text-xs tracking-wider">Vendeur</th>
                <th className="text-right px-5 py-3 font-semibold uppercase text-xs tracking-wider">Prospects</th>
                <th className="text-right px-5 py-3 font-semibold uppercase text-xs tracking-wider">Signés</th>
                <th className="text-right px-5 py-3 font-semibold uppercase text-xs tracking-wider">CA signé</th>
                <th className="text-right px-5 py-3 font-semibold uppercase text-xs tracking-wider">Taux</th>
              </tr>
            </thead>
            <tbody>
              {data.performance_vendeurs.map((v, i) => (
                <tr
                  key={i}
                  onClick={() => v.vendeur_id && navigate(`/prospects?vendeur_id=${v.vendeur_id}`)}
                  className={`border-t border-slate-100 transition-colors ${v.vendeur_id ? "hover:bg-amber-50 cursor-pointer" : ""}`}
                  data-testid={`perf-row-${i}`}
                >
                  <td className="px-5 py-3 font-medium">{v.vendeur_name}</td>
                  <td className="px-5 py-3 text-right">{v.total}</td>
                  <td className="px-5 py-3 text-right font-semibold text-emerald-700">{v.signes}</td>
                  <td className="px-5 py-3 text-right font-semibold text-[#111111]">{formatEUR(v.ca)}</td>
                  <td className="px-5 py-3 text-right text-slate-600">{v.total > 0 ? Math.round((v.signes/v.total)*100) : 0}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>
    </div>
  );
}

function AlertesJour() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);

  useEffect(() => {
    const pad = (n) => String(n).padStart(2, "0");
    const toStr = (dt) => `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
    const now = new Date();
    const today = toStr(now);
    const tmr = new Date(now); tmr.setDate(tmr.getDate() + 1);
    const tomorrow = toStr(tmr);
    Promise.all([
      api.get("/relances").then((r) => r.data).catch(() => []),
      api.get("/rdv-planifies").then((r) => r.data).catch(() => []),
    ]).then(([rel, rdv]) => {
      const out = [];
      rel.forEach((r) => {
        if (r.date_relance <= tomorrow) {
          out.push({ id: r.id, type: "relance", date: r.date_relance, nom: `${r.prenom} ${r.nom}`.trim(), urgent: r.en_retard || r.date_relance === today, when: r.en_retard ? "en retard" : r.date_relance === today ? "aujourd'hui" : "demain" });
        }
      });
      rdv.forEach((r) => {
        const d = (r.date_prochain_rdv || "").slice(0, 10);
        if (d === today || d === tomorrow) {
          out.push({ id: r.id, type: "rdv", date: d, heure: r.heure_prochain_rdv, nom: `${r.prenom} ${r.nom}`.trim(), urgent: d === today, when: d === today ? "aujourd'hui" : "demain" });
        }
      });
      out.sort((a, b) => (a.date === b.date ? (a.heure || "").localeCompare(b.heure || "") : a.date.localeCompare(b.date)));
      setItems(out);
    });
  }, []);

  if (!items || items.length === 0) return null;

  return (
    <div className="mb-6" data-testid="alertes-jour">
      <div className="bg-white border border-slate-200 rounded-sm p-5">
        <div className="flex items-center gap-2 mb-4">
          <BellRing className="w-4 h-4 text-[#B8873A]" />
          <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-700">À faire aujourd'hui & demain</h3>
          <span className="ml-auto text-xs text-slate-400">{items.length} action{items.length > 1 ? "s" : ""}</span>
        </div>
        <div className="space-y-2">
          {items.map((it) => (
            <button key={`${it.type}-${it.id}`} onClick={() => navigate(`/prospects/${it.id}`)} data-testid={`alerte-${it.type}-${it.id}`}
              className={`w-full flex items-center gap-3 text-left px-3 py-2 rounded-sm border transition-colors group ${it.urgent && it.when === "en retard" ? "border-red-200 bg-red-50 hover:border-red-300" : it.urgent ? "border-amber-200 bg-amber-50 hover:border-amber-300" : "border-slate-200 hover:border-[#B8873A]/50"}`}>
              {it.type === "rdv"
                ? <CalendarClock className={`w-4 h-4 shrink-0 ${it.urgent ? "text-amber-600" : "text-slate-400"}`} />
                : <PhoneCall className={`w-4 h-4 shrink-0 ${it.when === "en retard" ? "text-red-600" : it.urgent ? "text-amber-600" : "text-slate-400"}`} />}
              <span className="text-sm font-semibold text-[#111111] truncate flex-1">{it.nom}</span>
              <span className="text-xs text-slate-500 shrink-0">{it.type === "rdv" ? `RDV ${it.heure || ""}` : "Relance"}</span>
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0 ${it.when === "en retard" ? "bg-red-100 text-red-700" : it.when === "aujourd'hui" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}>{it.when}</span>
              <ChevronRight className="w-3.5 h-3.5 text-slate-300 group-hover:text-[#B8873A] shrink-0" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function ObjectifCard({ obj }) {
  if (!obj || (!obj.objectif_ca && !obj.objectif_signatures)) return null;
  const title = obj.scope === "personnel" ? "Mon objectif du mois" : "Objectif du mois (équipe)";
  const fmtEUR = (v) => formatEUR(v);
  return (
    <div className="mb-6" data-testid="dashboard-objectif">
      {obj.en_retard && (
        <div className="flex items-start gap-3 bg-amber-50 border border-amber-300 rounded-sm px-4 py-3 mb-3" data-testid="objectif-alert">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900">
            <span className="font-semibold">Vous êtes en retard sur votre objectif.</span>{" "}
            Il reste <span className="font-semibold">{obj.jours_restants} jour{obj.jours_restants > 1 ? "s" : ""}</span> ce mois-ci —
            accélérez vos relances et RDV pour revenir dans la course&nbsp;!
          </div>
        </div>
      )}
      <div className="bg-white border border-slate-200 rounded-sm p-5">
        <div className="flex items-center gap-2 mb-4">
          <Gauge className="w-4 h-4 text-[#B8873A]" />
          <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-700">{title}</h3>
          <span className="ml-auto text-xs text-slate-400">{obj.jours_restants} jour{obj.jours_restants > 1 ? "s" : ""} restant{obj.jours_restants > 1 ? "s" : ""}</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {obj.objectif_ca > 0 && (
            <MiniGauge label="Chiffre d'affaires" current={fmtEUR(obj.ca_mois)} target={fmtEUR(obj.objectif_ca)} pct={obj.progress_ca} expected={obj.attendu_pct} />
          )}
          {obj.objectif_signatures > 0 && (
            <MiniGauge label="Ventes signées" current={obj.signes_mois} target={obj.objectif_signatures} pct={obj.progress_signatures} expected={obj.attendu_pct} />
          )}
        </div>
      </div>
    </div>
  );
}

function MiniGauge({ label, current, target, pct, expected }) {
  const reached = pct >= 100;
  return (
    <div>
      <div className="flex items-baseline justify-between mb-2">
        <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</span>
        <span className={`text-sm font-bold ${reached ? "text-emerald-600" : "text-[#111111]"}`}>{pct}%</span>
      </div>
      <div className="relative h-3 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full transition-[width] duration-500 ${reached ? "bg-emerald-500" : "bg-[#B8873A]"}`} style={{ width: `${Math.min(pct, 100)}%` }} />
        {expected != null && expected < 100 && (
          <div className="absolute top-0 bottom-0 w-0.5 bg-slate-400" style={{ left: `${expected}%` }} title={`Rythme attendu: ${expected}%`} />
        )}
      </div>
      <div className="flex items-center justify-between mt-1.5 text-xs text-slate-500">
        <span className="font-semibold text-[#111111]">{current}</span>
        <span>Objectif {target}</span>
      </div>
    </div>
  );
}
